import { noTryAsync } from '@breakoutlearning/firebase-repository/util'
import { inLocalDev } from 'config/environment'
import { useRepository } from 'hooks/auth'
import { useState, useEffect } from 'react'
import { getAppCommitSha } from 'util/commitSha'

export function useCheckCommitFileVersion({
  checkOnFocus = true,
  intervalMs,
}: {
  checkOnFocus?: boolean
  intervalMs?: number
} = {}) {
  const repository = useRepository()
  const sha = getAppCommitSha()
  const [commit, setCommit] = useState<string | null>(null)
  const commitFile = '/commit.txt'

  useEffect(() => {
    if (inLocalDev()) return

    let isMounted = true
    const fetchCommitFile = async () => {
      const [data, error] = await noTryAsync(async () => {
        const response = await fetch(commitFile, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-store',
          },
        })
        if (!response.ok) {
          throw new Error(
            `Failed to fetch commit file: ${response.status} ${response.statusText}`
          )
        }
        return response.text()
      })

      if (error) {
        console.error('Error fetching commit file:', error)
        repository.logEvent('commit_file_fetch_error', {
          message: error.message,
        })
        return
      }

      if (isMounted && data) {
        setCommit(data.trim())
      }
    }

    fetchCommitFile()

    if (checkOnFocus) {
      window.addEventListener('focus', fetchCommitFile)
    }

    const interval = intervalMs
      ? setInterval(fetchCommitFile, intervalMs)
      : null

    return () => {
      if (checkOnFocus) {
        window.removeEventListener('focus', fetchCommitFile)
      }
      if (interval) clearInterval(interval)
      isMounted = false
    }
  }, [checkOnFocus, intervalMs, repository, sha])

  const isOutdatedVersion = commit !== null && commit !== sha

  return {
    commitSha: commit,
    isOutdatedVersion,
    checkingVersion: commit === null,
  }
}
